import React from 'react';
import { HandHeart, CheckCircle, Info } from 'lucide-react';

interface VoterPledgeProps {
  engagementData: any;
  updateEngagementData: (patch: any) => void;
  onNext: () => void;
  onPrevious: () => void;
  isSubmitting?: boolean;
}

export default function VoterPledge({
  engagementData,
  updateEngagementData,
  onNext,
  onPrevious,
  isSubmitting = false,
}: VoterPledgeProps) {
  const handlePledgeChange = (checked: boolean) => {
    updateEngagementData({
      voterPledge: checked
    });
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl p-8 max-w-2xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-green-500 to-teal-600 rounded-full mb-4">
          <HandHeart className="w-8 h-8 text-white" />
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-3">Make a Personal Voting Plan (Optional)</h1>
        <p className="text-gray-600 text-lg">Some people find it helpful to make a personal commitment to participate in elections.</p>
      </div>

      {/* Pledge Card */}
      <div className="bg-gradient-to-br from-green-50 to-teal-50 border-2 border-green-200 rounded-2xl p-6 mb-6">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-white rounded-full mb-4 shadow-md">
            <HandHeart className="w-6 h-6 text-green-600" />
          </div>
          <blockquote className="text-xl font-medium text-gray-800 italic leading-relaxed">
            "I plan to participate in the upcoming election."
          </blockquote>
        </div>

        {/* Pledge Checkbox */}
        <label className="group relative flex items-center p-4 bg-white rounded-xl cursor-pointer transition-all hover:shadow-md border-2 border-transparent hover:border-green-300">
          <input
            type="checkbox"
            checked={engagementData.voterPledge}
            onChange={(e) => handlePledgeChange(e.target.checked)}
            className="w-6 h-6 text-green-600 border-gray-300 rounded focus:ring-green-500 focus:ring-2"
          />
          <div className="ml-4 flex items-center flex-1">
            <span className="font-medium text-gray-900 text-lg">I'd like to make this personal pledge</span>
          </div>
          {engagementData.voterPledge && (
            <CheckCircle className="w-6 h-6 text-green-600" />
          )}
        </label>
      </div>

      {/* Important Note */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-8">
        <div className="flex items-start">
          <Info className="w-5 h-5 text-amber-600 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-amber-900 mb-1">Important Note</h3>
            <p className="text-sm text-amber-800 leading-relaxed">
              This is a personal commitment only. It does not support or oppose any candidate or party.
            </p>
          </div>
        </div>
      </div>

      {/* Compliance Note */}
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-8">
        <p className="text-xs text-gray-600 text-center leading-relaxed">
          123Vote is a non-partisan civic engagement platform. 
          This pledge is for personal motivation purposes only and does not constitute any form of political endorsement.
        </p>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4">
        <button
          onClick={onPrevious}
          className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-colors"
        >
          Back
        </button>
        <button
          onClick={onNext}
          disabled={isSubmitting}
          className="flex-1 px-6 py-3 bg-gradient-to-r from-green-600 to-teal-600 text-white font-medium rounded-xl hover:from-green-700 hover:to-teal-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {isSubmitting ? "Proceeding..." : "Next"}
        </button>
      </div>
    </div>
  );
}
